import { formatMoney } from 'theme';

// Drives both bundle purchase markups: the single-offer cards
// (bundle-offer-cards.liquid) and the list-mode selector
// (bundle-offers-selector.liquid). Two purchase options: "EVO only" (one-time,
// warranty allowed) and "+ subscription" (the single offer, or a dropdown of
// standalone bundle products in list mode). It switches the SINGLE product form's
// id + selling_plan; it never adds a second line item.
class BundleOffersWidget extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.baseVariantId = this.dataset.baseId;
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.baseCompare = parseInt(this.dataset.baseCompare, 10) || 0;

    this.priceData = this._parsePrices();

    // Resolve the radios by value rather than by id: the two markups this element
    // drives (bundle-offer-cards and bundle-offers-selector) name their ids
    // differently.
    this.oneTimeInput = this.querySelector('input[value="one-time"]');
    this.subscriptionInput = this.querySelector('input[value="subscription"]');
    this.sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    this.dropdown = this.querySelector('.subscription-dropdown');

    this.subsPriceEl = this.querySelector('.subscription__subs .subscription-label-price');
    this.subsCompareEl = this.querySelector('.subscription__subs .subscription-label-compare-at-price');
    this.subsRightEl = this.querySelector('.subscription__subs .subscription-label-right');
    this.subsSaveEl = this.querySelector('.subscription__subs .subscription-label-save-amount');

    this.warrantyCollapse = document.querySelector('[data-warranty-collapse]');
    this.warrantyInput = document.getElementById('WarrantyCheckbox');
    this._warrantyTransitionEnd = null;

    // Relocate the warranty block into the card layout (below the Scanner Only card)
    // so it expands in place there. The warranty input keeps its form/name, so
    // submission is unaffected. Skipped while this instance is hidden, so a hidden
    // one cannot steal the block from the visible one.
    if (this.warrantyCollapse && this._isActive()) {
      const slot = this.querySelector('[data-warranty-slot]');
      if (slot) slot.appendChild(this.warrantyCollapse);
    }

    this.currentOffer = this._readSelectedOffer();

    this._onOptionSelected = this._onOptionSelected.bind(this);
    this._onPurchaseOptionChange = this._onPurchaseOptionChange.bind(this);
    this._onWarrantyChange = this._onWarrantyChange.bind(this);

    // The <subscription-dropdown> bubbles this when a plan option is chosen.
    this.addEventListener('subscription-option-selected', this._onOptionSelected);
    this.oneTimeInput?.addEventListener('change', this._onPurchaseOptionChange);
    this.subscriptionInput?.addEventListener('change', this._onPurchaseOptionChange);
    this.warrantyInput?.addEventListener('change', this._onWarrantyChange);

    // Reflect the initial (subscription-default) state onto the form and prices.
    // No animation on load: the warranty starts collapsed via SSR (height: 0).
    this._applyState(false);
  }

  disconnectedCallback() {
    this.removeEventListener('subscription-option-selected', this._onOptionSelected);
    this.oneTimeInput?.removeEventListener('change', this._onPurchaseOptionChange);
    this.subscriptionInput?.removeEventListener('change', this._onPurchaseOptionChange);
    this.warrantyInput?.removeEventListener('change', this._onWarrantyChange);
  }

  _parsePrices() {
    try {
      return JSON.parse(document.getElementById('PricesData')?.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  _form() {
    return this.formId ? document.getElementById(this.formId) : null;
  }

  // Active = actually visible in layout. Robust to being hidden by an ancestor,
  // which is how the landing-page bundle-showcase ships its purchase column. Only a
  // visible instance owns its product form, buy-button price and warranty block.
  _isActive() {
    return this.getClientRects().length > 0;
  }

  // Read the currently-selected offer. In single-offer mode (custom.bundle_offer)
  // there is no dropdown, so the one offer rides on the element's data-offer-*
  // attributes. Otherwise read the selected dropdown option straight from the DOM
  // (robust even if two offers happen to share a selling-plan id).
  _readSelectedOffer() {
    if (this.dataset.offerVariantId) {
      return {
        variantId: this.dataset.offerVariantId,
        planId: this.dataset.offerPlanId || '',
        price: parseInt(this.dataset.offerPrice, 10) || 0,
        compare: parseInt(this.dataset.offerCompare, 10) || 0,
      };
    }
    const opt =
      this.dropdown?.querySelector('.subscription-dropdown-option.selected') ||
      this.dropdown?.querySelector('.subscription-dropdown-option');
    if (!opt) return null;
    return {
      variantId: opt.dataset.variantId,
      planId: opt.dataset.sellingPlanId || '',
      price: parseInt(opt.dataset.price, 10) || 0,
      compare: parseInt(opt.dataset.compare, 10) || 0,
    };
  }

  get isSubscription() {
    return !this.oneTimeInput || !this.oneTimeInput.checked;
  }

  _onOptionSelected() {
    this.currentOffer = this._readSelectedOffer();
    // Choosing a plan implies the subscription option.
    if (this.subscriptionInput && !this.subscriptionInput.checked) {
      this.subscriptionInput.checked = true;
    }
    this._applyState(true);
  }

  _onPurchaseOptionChange() {
    this._applyState(true);
  }

  _onWarrantyChange() {
    // Only a visible instance owns the buy-button price.
    if (!this._isActive()) return;
    // Toggling the warranty checkbox only affects the EVO-only total; the warranty
    // block is already open here, so do not re-run the collapse animation.
    this._updatePrices(this.isSubscription);
  }

  _applyState(animate) {
    // Hidden instance: leave the form alone and make sure our own selling_plan
    // input is not serialized on submit.
    if (!this._isActive()) {
      if (this.sellingPlanInput) this.sellingPlanInput.disabled = true;
      return;
    }

    const subs = this.isSubscription;

    // Switch the single line item.
    const form = this._form();
    const idInput =
      form?.querySelector('input[name="id"]') ||
      document.querySelector(`input[name="id"][form="${this.formId}"]`);

    if (subs && this.currentOffer) {
      if (idInput) {
        idInput.disabled = false;
        idInput.value = this.currentOffer.variantId;
      }
      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = false;
        this.sellingPlanInput.value = this.currentOffer.planId || '';
      }
    } else {
      if (idInput) {
        idInput.disabled = false;
        idInput.value = this.baseVariantId;
      }
      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = false;
        this.sellingPlanInput.value = '';
      }
    }

    // Warranty is offered only for the EVO-only (one-time) option; when a
    // subscription is selected it collapses and is excluded from the cart.
    this._setWarranty(!subs, animate);

    // The subscription block always shows the selected offer's price (matches the
    // app-bundle widget, whose subs block shows the bundle price regardless).
    this._updateSubsLabel();

    this._updatePrices(subs);
  }

  // Enable/disable the warranty input and animate its collapsing wrapper between
  // height 0 and the content height. With animate=false the state is applied
  // instantly (used on load, where the wrapper is already collapsed via SSR).
  _setWarranty(allow, animate) {
    if (this.warrantyInput) this.warrantyInput.disabled = !allow;

    const el = this.warrantyCollapse;
    if (!el) return;

    // Clear any pending expand-completion handler so it can't fire mid-collapse.
    if (this._warrantyTransitionEnd) {
      el.removeEventListener('transitionend', this._warrantyTransitionEnd);
      this._warrantyTransitionEnd = null;
    }

    if (!animate) {
      el.style.height = allow ? 'auto' : '0';
      return;
    }

    if (allow) {
      // Expand 0 -> content height, then release to auto so it stays responsive.
      el.style.height = `${el.scrollHeight}px`;
      const onEnd = (event) => {
        if (event.target !== el || event.propertyName !== 'height') return;
        el.style.height = 'auto';
        el.removeEventListener('transitionend', onEnd);
        this._warrantyTransitionEnd = null;
      };
      this._warrantyTransitionEnd = onEnd;
      el.addEventListener('transitionend', onEnd);
    } else {
      // Collapse: pin the current height in px, force reflow, then transition to 0.
      el.style.height = `${el.scrollHeight}px`;
      void el.offsetHeight;
      el.style.height = '0';
    }
  }

  _updateSubsLabel() {
    const offer = this.currentOffer;
    if (!offer) return;
    const hasSale = offer.compare > offer.price;
    if (this.subsPriceEl) this.subsPriceEl.textContent = formatMoney(offer.price);
    if (this.subsCompareEl) this.subsCompareEl.textContent = hasSale ? formatMoney(offer.compare) : '';
    if (this.subsRightEl) this.subsRightEl.classList.toggle('has-sale', hasSale);
    if (this.subsSaveEl) {
      const save = offer.compare - offer.price;
      if (save > 0) {
        const tpl = window.themeVariables?.strings?.saveBadge || '[savings]';
        this.subsSaveEl.textContent = `(${tpl.replace('[savings]', formatMoney(save))})`;
        this.subsSaveEl.classList.remove('hidden');
      } else {
        this.subsSaveEl.classList.add('hidden');
      }
    }
  }

  _updatePrices(subs) {
    let displayPrice;
    let displayCompare;

    if (subs && this.currentOffer) {
      displayPrice = this.currentOffer.price;
      displayCompare = this.currentOffer.compare;
    } else {
      const warranty =
        this.warrantyInput && this.warrantyInput.checked ? this.priceData.warrantyPrice || 0 : 0;
      displayPrice = this.basePrice + warranty;
      displayCompare = this.baseCompare > 0 ? this.baseCompare + warranty : 0;
    }
    const hasSale = displayCompare > displayPrice;

    // Buy-button price.
    const form = this._form();
    const btnText = form?.querySelector('buy-buttons:not([data-preorder]) .button-inner');
    if (btnText) {
      const tpl = window.themeVariables?.strings?.addToCartBtnPrice || 'Add to cart - [price]';
      btnText.innerHTML = tpl.replace('[price]', formatMoney(displayPrice));
    }

    // Main product price block (defensive: normally hidden on this PDP).
    const priceBlock = document.querySelector('.product-info__price');
    if (priceBlock) {
      const cur = priceBlock.querySelector('sale-price');
      const cmp = priceBlock.querySelector('compare-at-price');
      if (cur) {
        cur.textContent = formatMoney(displayPrice);
        cur.className = hasSale ? 'text-lg text-on-sale' : 'text-lg';
      }
      if (hasSale) {
        if (cmp) {
          cmp.textContent = formatMoney(displayCompare);
        } else if (cur) {
          const el = document.createElement('compare-at-price');
          el.className = 'text-subdued line-through';
          el.textContent = formatMoney(displayCompare);
          cur.after(el);
        }
      } else if (cmp) {
        cmp.remove();
      }
    }
  }
}

if (!window.customElements.get('bundle-offers-widget')) {
  window.customElements.define('bundle-offers-widget', BundleOffersWidget);
}
